import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  InlineAlert,
  OdorPath,
  Skeleton,
  TextField,
  type DataTableColumn,
} from "@/components";
import type { MaterialListItem } from "@/features/materials/api";
import { formatInteger, pluralize } from "@/lib/format";
import { useSearchParamsInPlace } from "@/lib/hooks";
import { useOdorTerm, useOdorTermMaterials, useOdorTree, type OdorTreeNode } from "./api";
import styles from "./KnowledgePage.module.css";

function matches(node: OdorTreeNode, filter: string): boolean {
  const text = filter.trim().toLocaleLowerCase();
  if (!text) return true;
  return node.name_en.toLocaleLowerCase().includes(text) || node.name_fr.toLocaleLowerCase().includes(text);
}

function TermButton({
  node,
  selected,
  onSelect,
}: {
  node: OdorTreeNode;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button type="button" className={styles.term} aria-current={selected} onClick={() => onSelect(node.id)}>
      <span className={styles.swatch} data-family={node.swatch ?? undefined} aria-hidden="true" />
      <span className={styles.termName}>{node.name_en}</span>
      <span className={styles.count}>{node.material_count || ""}</span>
    </button>
  );
}

function TermDetail({ termId, onSelect }: { termId: string; onSelect: (id: string) => void }) {
  const term = useOdorTerm(termId);
  const [page, setPage] = useState(0);
  const materials = useOdorTermMaterials(termId, page * 50);
  if (term.isPending) return <Skeleton lines={4} />;
  if (term.isError) {
    return (
      <InlineAlert
        tone="danger"
        title="Couldn’t load this term"
        action={<Button onClick={() => void term.refetch()}>Retry</Button>}
      >
        {term.error.message}
      </InlineAlert>
    );
  }
  const detail = term.data;
  const columns: DataTableColumn<MaterialListItem>[] = [
    {
      id: "name",
      header: "Material",
      width: "wide",
      cell: (row) => (
        <Link to={`/materials/${row.id}`} className={styles.materialLink}>
          {row.name}
        </Link>
      ),
    },
    { id: "cas", header: "CAS", width: "fit", mono: true, cell: (row) => row.cas ?? "—" },
    {
      id: "path",
      header: "Odor path",
      cell: (row) => (
        <OdorPath
          size="sm"
          terms={row.odor_path.map((item) => ({ id: item.id, label: item.name_en, family: item.swatch }))}
        />
      ),
    },
    {
      id: "stock",
      header: "Stock",
      width: "fit",
      cell: (row) =>
        row.owned ? (
          <Badge tone="success" icon="check">
            {pluralize(row.lot_count, "lot", "lots")}
          </Badge>
        ) : (
          <span className={styles.subtle}>Not owned</span>
        ),
    },
  ];
  const total = materials.data?.count ?? 0;
  return (
    <div className={styles.detail}>
      <header className={styles.termHeader}>
        {detail.path.length > 1 ? (
          <OdorPath terms={detail.path.map((item) => ({ id: item.id, label: item.name_en, family: item.swatch }))} />
        ) : null}
        <h2 className={styles.termTitle}>
          <span className={styles.swatch} data-family={detail.swatch ?? undefined} aria-hidden="true" />
          {detail.name_en}
          {detail.name_fr && detail.name_fr !== detail.name_en ? (
            <span className={styles.french} lang="fr">
              {detail.name_fr}
            </span>
          ) : null}
          <Badge>{detail.kind === "family" ? "Family" : "Descriptor"}</Badge>
        </h2>
        {detail.definition ? <p className={styles.definition}>{detail.definition}</p> : null}
        <span className={styles.subtle}>
          ScenTree vocabulary{detail.scentree_id ? `, id ${detail.scentree_id}` : ""}
        </span>
      </header>

      {detail.children.length ? (
        <section aria-labelledby="term-children">
          <h3 id="term-children" className={styles.sectionTitle}>
            Descriptors in this family
          </h3>
          <ul className={styles.chipList}>
            {detail.children.map((child) => (
              <li key={child.id}>
                <button type="button" className={styles.chipButton} onClick={() => onSelect(child.id)}>
                  {child.name_en}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {detail.aliases.length ? (
        <section aria-labelledby="term-aliases">
          <h3 id="term-aliases" className={styles.sectionTitle}>
            Source terms mapped here
          </h3>
          <ul className={styles.chipList}>
            {detail.aliases.map((alias) => (
              <li key={alias.text} className={styles.alias}>
                {alias.text}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-labelledby="term-materials">
        <h3 id="term-materials" className={styles.sectionTitle}>
          Materials with this term{" "}
          {materials.data ? <span className={styles.count}>{formatInteger(total)}</span> : null}
        </h3>
        <DataTable
          caption={`Materials whose odor path includes ${detail.name_en}`}
          columns={columns}
          rows={materials.data?.items}
          getRowId={(row) => row.id}
          loading={materials.isPending}
          error={materials.error}
          onRetry={() => void materials.refetch()}
          empty={
            <EmptyState
              variant="inline"
              headingLevel={3}
              title="No material carries this term yet"
              description="Odor paths come from ScenTree pages; a full ScenTree crawl fills in the rest."
            />
          }
        />
        {total > 50 ? (
          <div className={styles.pager}>
            <span className={styles.pagerText}>
              {formatInteger(page * 50 + 1)}–{formatInteger(Math.min(total, (page + 1) * 50))} of {formatInteger(total)}
            </span>
            <Button size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button size="sm" disabled={(page + 1) * 50 >= total} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

/** ScenTree's 17 families and their descriptors, with definitions and the materials that carry each term. */
export function VocabularyTab() {
  const tree = useOdorTree();
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const selected = searchParams.get("term");
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const families = useMemo(() => tree.data?.families ?? [], [tree.data]);
  const visible = useMemo(
    () =>
      families
        .map((family) => ({ family, children: family.children.filter((child) => matches(child, filter)) }))
        .filter(({ family, children }) => matches(family, filter) || children.length > 0),
    [families, filter],
  );
  const unplaced = (tree.data?.unplaced ?? []).filter((node) => matches(node, filter));

  function select(id: string) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("term", id);
      return next;
    });
  }

  function toggle(id: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (tree.isError) {
    return (
      <div className={styles.panel}>
        <InlineAlert
          tone="danger"
          title="Couldn’t load the odor vocabulary"
          action={<Button onClick={() => void tree.refetch()}>Retry</Button>}
        >
          {tree.error.message}
        </InlineAlert>
      </div>
    );
  }

  return (
    <div className={styles.split}>
      <nav className={styles.treePane} aria-label="Odor families and descriptors">
        <TextField
          label="Find a term"
          hideLabel
          size="sm"
          type="search"
          placeholder="Find a family or descriptor"
          prefix={<Icon name="search" />}
          value={filter}
          onValueChange={setFilter}
        />
        {tree.isPending ? (
          <Skeleton lines={10} />
        ) : families.length === 0 ? (
          <p className={styles.muted}>The vocabulary loads with the ScenTree seed.</p>
        ) : (
          <ul className={styles.tree}>
            {visible.map(({ family, children }) => {
              const open = expanded.has(family.id) || filter.trim().length > 0;
              return (
                <li key={family.id}>
                  <div className={styles.familyRow}>
                    <button
                      type="button"
                      className={styles.toggle}
                      aria-expanded={open}
                      aria-label={`${open ? "Collapse" : "Expand"} ${family.name_en}`}
                      onClick={() => toggle(family.id)}
                      disabled={children.length === 0}
                    >
                      <Icon name="chevronRight" />
                    </button>
                    <TermButton node={family} selected={selected === family.id} onSelect={select} />
                  </div>
                  {open && children.length ? (
                    <ul className={styles.descriptors}>
                      {children.map((child) => (
                        <li key={child.id}>
                          <TermButton node={child} selected={selected === child.id} onSelect={select} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
            {unplaced.length ? (
              <li>
                <div className={styles.groupLabel}>Not yet placed in a family</div>
                <ul className={styles.descriptors}>
                  {unplaced.map((node) => (
                    <li key={node.id}>
                      <TermButton node={node} selected={selected === node.id} onSelect={select} />
                    </li>
                  ))}
                </ul>
              </li>
            ) : null}
          </ul>
        )}
      </nav>
      <div>
        {selected ? (
          <TermDetail key={selected} termId={selected} onSelect={select} />
        ) : (
          <EmptyState
            variant="inline"
            icon="leaf"
            title="Choose a family or descriptor"
            description="Each term has a definition in English, its French name, and the materials whose ScenTree odor path includes it. Descriptors sit under the family they most often follow."
          />
        )}
      </div>
    </div>
  );
}
